/**
 * Client-side image compressie voor mobiele uploads.
 *
 * Schaalt foto's terug naar maxDim aan langste zijde en encodeert als JPEG.
 * Reden: een 12 MP iPhone-foto is 4-12 MB; voor bonfoto's heb je geen origineel
 * formaat nodig. Met compressie blijft de upload onder de FormData/server-action
 * body-limit en gaat de upload sneller op 4G.
 *
 * Niet-image input (bv. application/pdf) wordt ongemoeid gelaten.
 * Bij elke fout (geen canvas-context, decode-fout) wordt het origineel teruggegeven.
 */

const DEFAULT_MAX_DIM = 2000
const DEFAULT_QUALITY = 0.85

export async function compressImage(
  file: File,
  maxDim = DEFAULT_MAX_DIM,
  quality = DEFAULT_QUALITY,
  // FO-328 — true = altijd de JPEG-output teruggeven wanneer decoderen lukt,
  // ook als die groter is dan het origineel. Nodig voor formaat-CONVERSIE
  // (HEIC → JPEG): daar is "origineel behouden want kleiner" juist fout.
  forceerJpegOutput = false,
): Promise<File> {
  if (typeof window === 'undefined') return file
  if (!file.type.startsWith('image/')) return file

  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    let { width, height } = img
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height)
      width = Math.round(width * ratio)
      height = Math.round(height * ratio)
    }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(img, 0, 0, width, height)
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality)
    if (!blob) return file
    if (!forceerJpegOutput && blob.size >= file.size) return file
    const baseName = file.name.replace(/\.[^.]+$/, '')
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    })
  } catch {
    return file
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = src
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality)
  })
}
